/**
 * Labels for the dashboard (WP-10): a cost on each package card, on each verdict, and the run total. The
 * dashboard fetches `GET /v1/costs` (a {@link CostReport}) and passes it in; before it arrives, everything reads
 * as not loaded rather than $0.
 */
import type { Amount, CostReport, PackageCost, RunCost } from "../rollup.ts";

export type CostTone = "good" | "warn" | "muted";

export interface CostLabel {
  /** `$1.23`, `$0.42 (1 unreported)`, `unreported`, `-`, or `loading`. */
  text: string;
  tone: CostTone;
  /** Hover text: the breakdown and the price table date. */
  title: string;
}

const NOT_LOADED: CostLabel = { text: "loading", tone: "muted", title: "costs not loaded yet" };

function toneOf(a: Amount): CostTone {
  return a.status === "reported" && a.unpriced.length === 0 ? "good" : a.status === "none" ? "muted" : "warn";
}

function tokensLine(a: Amount): string {
  const t = a.tokens;
  return `${t.input.toLocaleString("en-AU")} in, ${t.output.toLocaleString("en-AU")} out, ${t.cacheWrite.toLocaleString("en-AU")} cache write, ${t.cacheRead.toLocaleString("en-AU")} cache read`;
}

function findPackage(report: CostReport, wpId: string): { run: RunCost; pkg: PackageCost } | null {
  for (const run of report.runs) {
    const pkg = run.packages.find((p) => p.wpId === wpId);
    if (pkg) return { run, pkg };
  }
  return null;
}

/** Cost of one package: agent plus verifier. */
export function packageCostLabel(report: CostReport | null, wpId: string): CostLabel {
  if (!report) return NOT_LOADED;
  const found = findPackage(report, wpId);
  if (!found) return { text: "-", tone: "muted", title: "no usage for this package" };
  const { pkg } = found;
  return {
    text: pkg.total.label,
    tone: toneOf(pkg.total),
    title: `agent ${pkg.agent.label}; verifier ${pkg.verifier.label}. ${tokensLine(pkg.total)}. Prices as of ${report.prices.asOf}.`,
  };
}

/** Cost of one verdict. */
export function verdictCostLabel(report: CostReport | null, verdict: { wpId: string; commit: string; createdAt: string }): CostLabel {
  if (!report) return NOT_LOADED;
  const v = report.runs.flatMap((r) => r.verdicts).find((x) => x.wpId === verdict.wpId && x.commit === verdict.commit && x.createdAt === verdict.createdAt);
  if (!v) return { text: "-", tone: "muted", title: "no usage for this verdict" };
  return { text: v.amount.label, tone: toneOf(v.amount), title: `${tokensLine(v.amount)}. Prices as of ${report.prices.asOf}.` };
}

/** The run total for a spec's run on this coordinator. */
export function runTotalLabel(report: CostReport | null, specId: string): CostLabel {
  if (!report) return NOT_LOADED;
  const run = report.runs.find((r) => r.specId === specId);
  if (!run) return { text: "-", tone: "muted", title: "no usage for this run" };
  const verdicts = run.verdicts.reduce((n, v) => n + v.amount.nanoUsd, 0);
  return {
    text: `run total ${run.total.label}`,
    tone: run.reconciled ? toneOf(run.total) : "warn",
    title: `${run.packages.length} packages and ${run.verdicts.length} verdicts${run.reconciled ? ", reconciled" : ", DOES NOT RECONCILE"}. Verifier share ${((verdicts / Math.max(1, run.total.nanoUsd)) * 100).toFixed(0)}%. Prices as of ${report.prices.asOf}.`,
  };
}
